import { Check, CheckCheck, AlertTriangle, Loader2, RotateCw, Sparkles } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { OndaAvatar } from "./OndaAvatar";
import { VerifiedBadge } from "./VerifiedBadge";

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

export function MessageBubble({ message, mine, sender, showSenderInfo, onRetry }) {
  const isRead = !!message.read_at;
  const status = message.__status || "sent";
  const isSending = status === "sending";
  const isFailed = status === "failed";
  const isAuto = !!message.is_auto;

  return (
    <div
      className={"flex gap-2 " + (mine ? "justify-end" : "justify-start")}
      data-testid={`message-${message.id}`}
    >
      {!mine && showSenderInfo && sender && (
        <OndaAvatar src={sender.avatar} name={sender.display_name} size={28} className="mt-4 shrink-0" />
      )}
      <div className={"max-w-[80%] " + (mine ? "items-end" : "items-start") + " flex flex-col"}>
        {!mine && showSenderInfo && sender && (
          <div className="flex items-center gap-1 mb-0.5 px-1">
            <span
              className="text-[11px] font-semibold q-accent-text font-mono truncate"
              data-testid={`message-sender-${message.id}`}
            >
              @{sender.ondatag}
            </span>
            {sender.is_verified_business && <VerifiedBadge size={10} />}
          </div>
        )}
        <div
          className={
            "rounded-2xl px-4 py-2.5 shadow-sm " +
            (mine
              ? "rounded-br-sm text-[color:var(--accent-contrast)]"
              : "rounded-bl-sm bg-white/[0.06] border border-white/10 text-slate-100") +
            (isFailed ? " ring-2 ring-amber-500/60" : "") +
            (isSending ? " opacity-80" : "")
          }
          style={
            mine && !isFailed
              ? { background: "var(--accent)" }
              : isFailed
                ? { background: "rgba(120,80,30,0.35)" }
                : undefined
          }
        >
          <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
            {message.text}
          </div>
          <div
            className={
              "flex items-center gap-1 justify-end mt-1 text-[10px] font-mono " +
              (mine ? "text-[color:var(--accent-contrast)]/70" : "text-slate-400")
            }
          >
            <span data-testid={`message-time-${message.id}`}>{fmtTime(message.timestamp)}</span>
            {isAuto && (
              <span
                className="inline-flex items-center gap-0.5 ml-1 tracking-normal lowercase"
                data-testid={`message-auto-${message.id}`}
              >
                <Sparkles size={9} /> auto
              </span>
            )}
            {mine && (
              <span
                data-testid={`message-status-${message.id}`}
                className="inline-flex items-center"
              >
                {isSending ? (
                  <Loader2 size={12} strokeWidth={2.4} className="ml-0.5 animate-spin" />
                ) : isFailed ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => onRetry && onRetry(message)}
                          data-testid={`message-retry-${message.id}`}
                          className="ml-1 inline-flex items-center gap-1 text-amber-300 hover:text-amber-200"
                          aria-label="Reintentar envío"
                        >
                          <AlertTriangle size={12} strokeWidth={2.4} />
                          <RotateCw size={12} strokeWidth={2.4} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="bg-[#111c33]/95 border-white/10 text-slate-100 text-xs"
                      >
                        No se pudo enviar. Toca para reintentar.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : isRead ? (
                  <CheckCheck
                    size={13}
                    strokeWidth={2.4}
                    className="ml-0.5"
                    style={{ color: "var(--accent-contrast)" }}
                  />
                ) : (
                  <Check size={13} strokeWidth={2.4} className="ml-0.5" />
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
